import { useState, useEffect } from 'react';
import { Form, Input, InputNumber, DatePicker, Select, Button, Space, Switch, Tag, message, Typography, App } from 'antd';
import { PlusOutlined, DeleteOutlined, StarOutlined, StarFilled, ReadOutlined } from '@ant-design/icons';
import { useApi } from '../../hooks/use_api';
import { useNodeInfo } from '../../hooks/use_node_info';
import { usePersonFields } from '../../hooks/use_person_fields';
import GeoLocationInput from './GeoLocationInput';

// Apps are not created here: an app instance comes into being when it is
// installed in an entity (docs/october-model-todos.md 3.2, 3.11).
export const ENTITY_TYPES = [
    { value: 'context', label: 'Context' },
    { value: 'relationship', label: 'Relationship' },
    { value: 'person', label: 'Person' },
];

// The form's "When" value → the entity's window (todo D12). The picker gives
// [from, until] with either end open; a person never has one, so the field is
// hidden for people and this yields two nulls, which the backend treats as unset.
export function windowFields(when) {
    return {
        starts_at: when?.[0] ? when[0].toISOString() : null,
        ends_at: when?.[1] ? when[1].toISOString() : null,
    };
}

function CreateEntityForm({ form, onFinish, loading, submitLabel = 'Create', onCancel, defaultEntityType = 'context', disableEntityType = false, showListedToggle = false, onListedChange, listedLoading = false }) {
    const api = useApi();
    const { modal } = App.useApp();
    const { enabledEntityTypes, featureMultipleDescriptions } = useNodeInfo();
    // Tier 3 gate (#36): only offer entity types this node has enabled. null =>
    // not yet known, so show all (the backend still enforces the real gate).
    const typeOptions = enabledEntityTypes
        ? ENTITY_TYPES.filter(t => enabledEntityTypes.includes(t.value))
        : ENTITY_TYPES;
    // requested default may not be one of this node's enabled types (e.g. a
    // node with only "person" enabled but no defaultEntityType override) —
    // fall back to the first type this node actually allows.
    const effectiveDefaultType = typeOptions.some(t => t.value === defaultEntityType)
        ? defaultEntityType
        : (typeOptions[0]?.value ?? defaultEntityType);
    const personFields = usePersonFields();
    const [extracting, setExtracting] = useState(false);
    const watchedDescs = Form.useWatch('descriptions', form) || [];
    const watchedEntityType = Form.useWatch('entity_type', form) || effectiveDefaultType;
    const showPersonFields = watchedEntityType === 'person' && personFields.length > 0;
    // A person is not a web page: URL (and its extract-name-and-description
    // helper) is for contexts. Entity.url still exists on the model,
    // it just isn't offered here — see BUILT_IN_FIELDS in Account/PersonFields.
    const showUrl = watchedEntityType !== 'person';
    // Same rule as showUrl: email is not a Person field. An address still
    // reaches a person through CSV import, which is what makes their claim
    // link sendable — see CSVImportModal.
    const showEmail = watchedEntityType !== 'person';
    // An occasion (todo D12): a group with a start or an end. Same rule as the
    // entity page's When row — contexts and relationships, never people.
    const showWhen = ['context', 'relationship'].includes(watchedEntityType);

    // enabledEntityTypes loads asynchronously (see useNodeInfo), so it can resolve
    // after this form's initialValue has already been applied. If the currently
    // selected type falls outside the node's allowed types, snap it to one that's
    // actually allowed. Skipped for edit forms (disableEntityType) since those
    // display an existing entity's immutable type rather than picking a default.
    useEffect(() => {
        if (disableEntityType || !typeOptions.length) return;
        const current = form.getFieldValue('entity_type');
        if (!typeOptions.some(t => t.value === current)) {
            form.setFieldValue('entity_type', effectiveDefaultType);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enabledEntityTypes]);

    const handleExtract = async () => {
        const url = form.getFieldValue('url')?.trim();
        if (!url) { message.warning('Enter a URL first'); return; }
        setExtracting(true);
        let data;
        try {
            data = await api.post('/api/entities/extract-url', { url });
        } catch (err) {
            message.error(err.message || 'Failed to reach extraction service');
            setExtracting(false);
            return;
        }
        try {
            if (data.title && !form.getFieldValue('name')) {
                form.setFieldValue('name', data.title);
            }
            if (data.text) {
                modal.confirm({
                    title: 'Use extracted text as description?',
                    width: 520,
                    content: (
                        <div style={{ maxHeight: 200, overflowY: 'auto', marginTop: 8 }}>
                            <Typography.Paragraph style={{ whiteSpace: 'pre-wrap', fontSize: 12 }}>
                                {data.text}
                            </Typography.Paragraph>
                        </div>
                    ),
                    okText: 'Use as description',
                    cancelText: 'Skip',
                    onOk: () => {
                        const descs = form.getFieldValue('descriptions') || [];
                        const updated = descs.map((d, i) => i === 0 ? { ...d, content: data.text } : d);
                        form.setFieldsValue({ descriptions: updated });
                    },
                });
            } else {
                message.success('Content extracted');
            }
        } catch {
            message.error('Failed to reach extraction service');
        } finally {
            setExtracting(false);
        }
    };

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
                label="Name"
                name="name"
                rules={[{ required: true, message: 'Please enter a name' }]}
            >
                <Input placeholder="Entity name" />
            </Form.Item>

            <Form.Item label="Entity Type" name="entity_type" initialValue={effectiveDefaultType}>
                <Select options={typeOptions} disabled={disableEntityType} />
            </Form.Item>

            {showListedToggle && (
                <Form.Item
                    name="is_listed"
                    valuePropName="checked"
                    initialValue={true}
                    label="Listed"
                    tooltip="Listed entities appear in public/discoverable views. Unlisted entities are only visible to people who already know them."
                >
                    <Switch checkedChildren="Listed" unCheckedChildren="Unlisted" onChange={onListedChange} loading={listedLoading} />
                </Form.Item>
            )}

            {showUrl && (
                <Form.Item
                    label="URL"
                    name="url"
                    rules={[{ type: 'url', message: 'Please enter a valid URL' }]}
                >
                    <Space.Compact style={{ width: '100%' }}>
                        <Input placeholder="https://example.com" />
                        <Button icon={<ReadOutlined />} loading={extracting} onClick={handleExtract} title="Extract name & description from URL" />
                    </Space.Compact>
                </Form.Item>
            )}

            {showEmail && (
                <Form.Item
                    label="Email"
                    name="email"
                    rules={[{ type: 'email', message: 'Please enter a valid email' }]}
                >
                    <Input placeholder="contact@example.com" />
                </Form.Item>
            )}

            {showWhen && (
                <Form.Item
                    label="When"
                    name="when"
                    tooltip="Optional. A group with a start or an end is an occasion; the calendar shows it from its start. Leave both open for an ongoing group."
                    // Drop the dates if the type is switched to person, where the
                    // field is hidden — a person never carries a window.
                    preserve={false}
                    rules={[{
                        // Mirrors the backend's rule (schemas/entities.py _Window).
                        validator: (_, v) => v?.[0] && v?.[1] && !v[1].isAfter(v[0])
                            ? Promise.reject(new Error('Until must be after From'))
                            : Promise.resolve(),
                    }]}
                >
                    <DatePicker.RangePicker
                        style={{ width: '100%' }}
                        allowEmpty={[true, true]}
                        placeholder={['From (open)', 'Until (open)']}
                    />
                </Form.Item>
            )}

            {showPersonFields && personFields.map(f => (
                <Form.Item key={f.key} label={f.label} name={['person_fields', f.key]}>
                    {f.field_type === 'number' ? (
                        <InputNumber style={{ width: '100%' }} placeholder={f.label} />
                    ) : f.field_type === 'date' ? (
                        <DatePicker style={{ width: '100%' }} placeholder={f.label} />
                    ) : f.field_type === 'geo' ? (
                        <GeoLocationInput />
                    ) : f.field_type === 'geo_multi' ? (
                        <GeoLocationInput multiple />
                    ) : f.field_type === 'select' ? (
                        <Select allowClear placeholder={f.label} options={(f.options || []).map(o => ({ value: o, label: o }))} />
                    ) : (
                        <Input placeholder={f.label} />
                    )}
                </Form.Item>
            ))}

            <Form.List name="descriptions" initialValue={[{ label: 'default', content: '', is_default: true }]}>
                {(fields, { add, remove }) => (
                    <div>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                            <span style={{ fontWeight: 500 }}>{featureMultipleDescriptions ? 'Profile — descriptions' : 'Profile'}</span>
                            {/* Extra labelled descriptions are behind feature_multiple_descriptions. */}
                            {featureMultipleDescriptions && (
                                <Button size="small" icon={<PlusOutlined />} onClick={() => add({ label: '', content: '', is_default: false })}>
                                    Add
                                </Button>
                            )}
                        </div>
                        {fields.map((field, index) => {
                            const isDefault = watchedDescs[field.name]?.is_default;
                            return (
                                <div key={field.key} style={{ border: '1px solid var(--border-color)', borderRadius: 6, padding: '10px 12px', marginBottom: 8 }}>
                                    <Form.Item name={[field.name, 'id']} noStyle hidden>
                                        <Input />
                                    </Form.Item>
                                    <Form.Item name={[field.name, 'is_default']} noStyle hidden>
                                        <Input />
                                    </Form.Item>
                                    {featureMultipleDescriptions && (
                                        <div style={{ display: 'flex', gap: 8, marginBottom: 6, alignItems: 'center' }}>
                                            <Form.Item name={[field.name, 'label']} noStyle>
                                                <Input
                                                    placeholder={isDefault ? 'default' : 'label (e.g. public)'}
                                                    style={{ flex: 1 }}
                                                    disabled={isDefault}
                                                />
                                            </Form.Item>
                                            {isDefault
                                                ? <Tag color="blue" icon={<StarFilled />}>default</Tag>
                                                : (
                                                    <Button
                                                        size="small"
                                                        type="text"
                                                        icon={<StarOutlined />}
                                                        title="Set as default"
                                                        onClick={() => {
                                                            const vals = form.getFieldValue('descriptions');
                                                            form.setFieldsValue({
                                                                descriptions: vals.map((d, i) => ({ ...d, is_default: i === index })),
                                                            });
                                                        }}
                                                    />
                                                )
                                            }
                                            {!isDefault && (
                                                <Button size="small" type="text" danger icon={<DeleteOutlined />} onClick={() => remove(field.name)} />
                                            )}
                                        </div>
                                    )}
                                    <Form.Item name={[field.name, 'content']} noStyle>
                                        <Input.TextArea rows={2} placeholder="Description content" />
                                    </Form.Item>
                                </div>
                            );
                        })}
                        {fields.length === 0 && (
                            <div style={{ color: 'var(--text-secondary)', fontSize: 13 }}>No descriptions yet. Click Add to create one.</div>
                        )}
                    </div>
                )}
            </Form.List>

            <Form.Item
                label="Attributes (JSON)"
                name="attributes"
                style={{ marginTop: 8 }}
                rules={[{
                    validator: (_, value) => {
                        if (!value) return Promise.resolve();
                        try { JSON.parse(value); return Promise.resolve(); }
                        catch { return Promise.reject('Invalid JSON'); }
                    }
                }]}
            >
                <Input.TextArea rows={2} placeholder='{"key": "value"}' />
            </Form.Item>

            <Form.Item style={{ marginBottom: 0 }}>
                <Space>
                    <Button type="primary" htmlType="submit" loading={loading}>{submitLabel}</Button>
                    {onCancel && <Button onClick={onCancel}>Cancel</Button>}
                </Space>
            </Form.Item>
        </Form>
    );
}

export default CreateEntityForm;
